import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AirplaneTilt, Bed, Train, Bus, ChartBar, Receipt,
  ShieldCheck, Users, Tag, Plus, Trash, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ADMIN_ROLES = new Set([
  "super_admin", "company_admin", "country_head", "region_head",
  "branch_manager", "sub_manager", "assistant_manager",
]);
const PARTNER_ROLES = new Set(["travel_partner"]);

const STATUS_COLOR = {
  draft:       "bg-zinc-100 text-zinc-700",
  submitted:   "bg-amber-100 text-amber-800",
  in_approval: "bg-amber-100 text-amber-800",
  approved:    "bg-emerald-100 text-emerald-800",
  booking:     "bg-blue-100 text-blue-800",
  booked:      "bg-purple-100 text-purple-800",
  completed:   "bg-zinc-100 text-zinc-700",
  rejected:    "bg-red-100 text-red-800",
  cancelled:   "bg-zinc-100 text-zinc-600",
};

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Travel() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const isPartner = PARTNER_ROLES.has(user?.role);

  const tabs = useMemo(() => {
    const t = [{ k: "my", l: "My Trips", icon: AirplaneTilt }];
    if (isAdmin) t.push({ k: "all", l: "All Trips", icon: AirplaneTilt });
    if (isAdmin) t.push({ k: "categories", l: "Categories", icon: Tag });
    if (isAdmin) t.push({ k: "policy",     l: "Policy", icon: ShieldCheck });
    if (isAdmin) t.push({ k: "bookings",   l: "Bookings", icon: Receipt });
    if (isAdmin) t.push({ k: "reports",    l: "Reports", icon: ChartBar });
    if (isPartner) t.push({ k: "partner",  l: "Partner Uploads", icon: Receipt });
    return t;
  }, [isAdmin, isPartner]);

  const [tab, setTab] = useState(tabs[0].k);

  return (
    <AppShell title="Travel">
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`trv-tab-${t.k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab === t.k ? "border-zinc-950 text-zinc-950 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "my"         && <MyTripsTab/>}
      {tab === "all"        && <AllTripsTab/>}
      {tab === "categories" && <CategoriesTab/>}
      {tab === "policy"     && <PolicyTab/>}
      {tab === "bookings"   && <BookingsTab/>}
      {tab === "reports"    && <ReportsTab/>}
      {tab === "partner"    && <PartnerUploadsTab/>}
    </AppShell>
  );
}


// ───────────────────────────── My Trips ─────────────────────────────────
function MyTripsTab() {
  const [trips, setTrips] = useState([]);
  const [elig, setElig] = useState(null);
  const load = async () => {
    try {
      const [t, e] = await Promise.all([
        api.get("/travel-requests"), api.get("/travel/me/eligibility"),
      ]);
      setTrips(t.data); setElig(e.data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    try {
      await api.post(`/travel-requests/${id}/submit`, {});
      toast.success("Submitted for approval");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.policy_check) {
        toast.error(`Hard policy block — ${detail.policy_check.violations.length} violations`);
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const c = elig?.category;
  return (
    <div className="space-y-5">
      {c && <EligibilityCard category={c} />}
      <SectionCard
        title="My travel"
        subtitle="Plan a new trip or track an existing one."
        icon={AirplaneTilt}
        action={<Link to="/app/travel/plan"><Button data-testid="trv-plan-cta">+ Plan a trip</Button></Link>}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Purpose</TableHead><TableHead>Itinerary</TableHead>
              <TableHead>Dates</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Est.</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trips.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-6">
                No trips yet — start with “Plan a trip”.
              </TableCell></TableRow>
            )}
            {trips.map(t => (
              <TableRow key={t.id} data-testid={`my-trip-${t.id}`}>
                <TableCell><div className="font-medium">{t.purpose}</div></TableCell>
                <TableCell className="text-xs space-y-0.5">
                  {(t.legs || []).map(l => (
                    <div key={l.id} className="flex items-center gap-1">
                      {iconFor(l.type)}<span>{l.from_city} → {l.to_city || l.from_city} · {l.vendor_name}</span>
                    </div>
                  ))}
                </TableCell>
                <TableCell className="text-xs">{t.start_date} → {t.end_date}</TableCell>
                <TableCell><Badge className={STATUS_COLOR[t.status]}>{t.status}</Badge></TableCell>
                <TableCell className="text-right tabular-nums">{inr(t.estimated_total)}</TableCell>
                <TableCell>
                  {t.status === "draft" && (
                    <Button size="sm" onClick={() => submit(t.id)} data-testid={`trv-submit-${t.id}`}>Submit</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
    </div>
  );
}

function EligibilityCard({ category }) {
  return (
    <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="elig-card">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700">
          <ShieldCheck size={22}/>
        </div>
        <div className="flex-1">
          <div className="text-xs text-zinc-500 uppercase tracking-wide">Your travel eligibility</div>
          <div className="text-lg font-semibold">{category.name}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
            <Cap label="Flight" cap={category.flight_cap} cls={category.flight_class_max} />
            <Cap label="Hotel/night" cap={category.hotel_cap_per_night} cls={`≤ ${category.hotel_star_max}★`}/>
            <Cap label="Train" cap={category.train_cap} cls={category.train_class_max}/>
            <Cap label="Bus" cap={category.bus_cap}/>
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            Allowed modes: {(category.allowed_modes||[]).join(", ")} ·
            {" Per diem: "}{inr(category.per_diem)}/day ·
            {" Min advance booking: "}{category.min_advance_days}d
          </div>
        </div>
      </div>
    </Card>
  );
}
function Cap({ label, cap, cls }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-500 uppercase">{label}</div>
      <div className="font-medium">{cap ? inr(cap) : "Unlimited"}</div>
      {cls && <div className="text-[11px] text-zinc-500">{cls}</div>}
    </div>
  );
}

function iconFor(t) {
  const S = { size: 12, className: "text-zinc-500" };
  if (t === "flight") return <AirplaneTilt {...S}/>;
  if (t === "hotel")  return <Bed {...S}/>;
  if (t === "train")  return <Train {...S}/>;
  if (t === "bus")    return <Bus {...S}/>;
  return <MapPin {...S}/>;
}


// ───────────────────────────── All Trips ────────────────────────────────
function AllTripsTab() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get("/travel-requests")).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    const body = { decision };
    if (decision === "reject") body.reason = prompt("Reason?") || "";
    try {
      await api.post(`/travel-requests/${id}/decide`, body);
      toast.success("Updated"); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const book = async (id) => {
    try {
      const r = await api.post(`/travel/requests/${id}/book`, {});
      toast.success(`Booked ${r.data.bookings_created} legs`); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <SectionCard title="All travel requests" icon={AirplaneTilt}>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Trip</TableHead><TableHead>Employee</TableHead><TableHead>Status</TableHead>
          <TableHead>Policy</TableHead><TableHead className="text-right">Est.</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map(r => {
            const ts = (r.policy_check || {}).tier_summary || {};
            return (
              <TableRow key={r.id} data-testid={`all-trip-${r.id}`}>
                <TableCell><div className="font-medium">{r.purpose}</div>
                  <div className="text-xs text-zinc-500">{r.start_date} → {r.end_date} · {(r.legs||[]).length} legs</div>
                </TableCell>
                <TableCell>{r.employee_name}</TableCell>
                <TableCell><Badge className={STATUS_COLOR[r.status]}>{r.status}</Badge></TableCell>
                <TableCell className="text-xs">
                  {ts.hard ? <Badge variant="destructive">{ts.hard} hard</Badge> : null}
                  {ts.soft ? <Badge className="bg-amber-100 text-amber-800 ml-1">{ts.soft} soft</Badge> : null}
                  {ts.advisory ? <Badge className="bg-sky-100 text-sky-800 ml-1">{ts.advisory} info</Badge> : null}
                  {!ts.hard && !ts.soft && !ts.advisory && <span className="text-emerald-600">Clean</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.estimated_total)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    {r.status === "in_approval" && <>
                      <Button size="sm" onClick={() => decide(r.id, "approve")} data-testid={`trv-approve-${r.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")} data-testid={`trv-reject-${r.id}`}>Reject</Button>
                    </>}
                    {r.status === "approved" && (
                      <Button size="sm" onClick={() => book(r.id)} data-testid={`trv-book-${r.id}`}>Book now</Button>
                    )}
                    {r.status === "booked" && (
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "complete")} data-testid={`trv-complete-${r.id}`}>Complete</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </SectionCard>
  );
}


// ───────────────────────────── Categories Tab ───────────────────────────
function CategoriesTab() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get("/travel/categories")).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <SectionCard title="Travel categories" subtitle="Per-employee caps & allowed modes." icon={Tag}>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Modes</TableHead>
          <TableHead className="text-right">Flight</TableHead><TableHead className="text-right">Hotel/night</TableHead>
          <TableHead className="text-right">Train</TableHead><TableHead className="text-right">Trip total</TableHead>
          <TableHead className="text-right">Adv. days</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map(c => (
            <TableRow key={c.id}>
              <TableCell><span className="font-mono text-xs">{c.code}</span> {c.is_default && <Badge className="ml-1 bg-emerald-100 text-emerald-800">default</Badge>}</TableCell>
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell className="text-xs">{(c.allowed_modes||[]).join(", ")}</TableCell>
              <TableCell className="text-right tabular-nums">{c.flight_cap ? inr(c.flight_cap) : "—"}<div className="text-[10px] text-zinc-500">{c.flight_class_max}</div></TableCell>
              <TableCell className="text-right tabular-nums">{c.hotel_cap_per_night ? inr(c.hotel_cap_per_night) : "—"}<div className="text-[10px] text-zinc-500">≤ {c.hotel_star_max}★</div></TableCell>
              <TableCell className="text-right tabular-nums">{c.train_cap ? inr(c.train_cap) : "—"}<div className="text-[10px] text-zinc-500">{c.train_class_max}</div></TableCell>
              <TableCell className="text-right tabular-nums">{c.trip_total_cap ? inr(c.trip_total_cap) : "Unltd"}</TableCell>
              <TableCell className="text-right">≥ {c.min_advance_days}d</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="text-xs text-zinc-500 mt-3">
        💡 In v1.1 we'll add Create / Edit forms here. For now, these 6 templates seed automatically and cover most companies.
      </div>
    </SectionCard>
  );
}


// ───────────────────────────── Policy Tab ───────────────────────────────
function PolicyTab() {
  const [policy, setPolicy] = useState(null);
  const load = async () => {
    try { setPolicy((await api.get("/travel/policy")).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  if (!policy) return null;
  const TIER = { hard: "🛑 Hard block", soft: "⚠️ Extra approval", advisory: "ℹ️ Advisory" };
  return (
    <SectionCard title="Travel policy rules" subtitle="3-tier enforcement: hard / soft / advisory." icon={ShieldCheck}>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Rule</TableHead><TableHead>Tier</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {(policy.rules || []).map(r => (
            <TableRow key={r.id}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell><Badge className={r.tier === "hard" ? "bg-red-100 text-red-800" : r.tier === "soft" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}>{TIER[r.tier]}</Badge></TableCell>
              <TableCell className="text-xs text-zinc-600">{r.description}</TableCell>
              <TableCell>{r.enabled ? <Badge className="bg-emerald-100 text-emerald-800">Enabled</Badge> : <Badge className="bg-zinc-100 text-zinc-700">Disabled</Badge>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="text-xs text-zinc-500 mt-3">
        💡 Rule editing UI ships in v1.1. Edit via API for now — see `/api/travel/policy` PUT.
      </div>
    </SectionCard>
  );
}


// ───────────────────────────── Bookings Tab ─────────────────────────────
function BookingsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => {
    try { setRows((await api.get("/travel/bookings")).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  })(); }, []);
  return (
    <SectionCard title="All bookings" subtitle="Confirmed PNRs across the company." icon={Receipt}>
      <Table>
        <TableHeader><TableRow>
          <TableHead>PNR</TableHead><TableHead>Type</TableHead><TableHead>Vendor</TableHead>
          <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">GST</TableHead>
          <TableHead>State</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-6">No bookings yet.</TableCell></TableRow>}
          {rows.map(b => (
            <TableRow key={b.id}>
              <TableCell className="font-mono text-xs">{b.pnr}</TableCell>
              <TableCell>{b.type}</TableCell>
              <TableCell>{b.vendor_name}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(b.paid_amount)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(b.gst_amount)}</TableCell>
              <TableCell className="text-xs">{b.gst_state}</TableCell>
              <TableCell><Badge className={b.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100"}>{b.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SectionCard>
  );
}


// ───────────────────────────── Reports Tab ──────────────────────────────
function ReportsTab() {
  const [data, setData] = useState({});
  useEffect(() => { (async () => {
    try {
      const results = await Promise.all([
        api.get("/travel/reports/spend-summary?days=365"),
        api.get("/travel/reports/spend-by-department?days=365"),
        api.get("/travel/reports/spend-by-mode?days=365"),
        api.get("/travel/reports/top-travellers?days=365"),
        api.get("/travel/reports/top-routes?days=365"),
        api.get("/travel/reports/gst-credit?days=365"),
        api.get("/travel/reports/policy-violations?days=365"),
        api.get("/travel/reports/approval-tat?days=365"),
      ]);
      setData({
        summary: results[0].data, dept: results[1].data, mode: results[2].data,
        travellers: results[3].data, routes: results[4].data, gst: results[5].data,
        violations: results[6].data, tat: results[7].data,
      });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  })(); }, []);

  if (!data.summary) return <div className="text-zinc-500">Loading…</div>;
  const s = data.summary;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total spend (365d)" value={inr(s.total_spend)} />
        <Kpi label="Trips" value={s.trips} />
        <Kpi label="Bookings" value={s.bookings} />
        <Kpi label="Avg / trip" value={inr(s.avg_per_trip)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Spend by department" icon={Users}>
          <MiniTable rows={data.dept.rows} cols={[["department","Department"],["spend","Spend",inr],["trips","Trips"]]} empty="No data"/>
        </SectionCard>
        <SectionCard title="Spend by mode" icon={AirplaneTilt}>
          <MiniTable rows={data.mode.rows} cols={[["mode","Mode"],["spend","Spend",inr],["count","#"]]} empty="No data"/>
        </SectionCard>
        <SectionCard title="Top travellers" icon={Users}>
          <MiniTable rows={data.travellers.rows} cols={[["employee_name","Employee"],["trips","Trips"],["spend","Spend",inr]]} empty="No data"/>
        </SectionCard>
        <SectionCard title="Top routes" icon={MapPin}>
          <MiniTable rows={data.routes.rows} cols={[["route","Route"],["count","Trips"],["spend","Spend",inr]]} empty="No data"/>
        </SectionCard>
        <SectionCard title="GST input credit" icon={Receipt}>
          <div className="text-sm font-medium mb-2">Total claimable: {inr(data.gst.total_claimable_gst)}</div>
          <MiniTable rows={data.gst.rows} cols={[["state","State"],["claimable_gst","GST",inr],["bookings","#"]]} empty="No data"/>
        </SectionCard>
        <SectionCard title="Approval TAT" icon={ChartBar}>
          {data.tat.samples ? (
            <div className="text-sm space-y-1">
              <div>Average: <span className="font-medium">{data.tat.avg_hours}h</span></div>
              <div>P95: <span className="font-medium">{data.tat.p95_hours}h</span></div>
              <div className="text-zinc-500">Based on {data.tat.samples} approved trips</div>
            </div>
          ) : <div className="text-zinc-500 text-sm">No approved trips in window.</div>}
        </SectionCard>
      </div>

      <SectionCard title="Policy violations" subtitle="Most recent first — hard/soft/advisory tier" icon={ShieldCheck}>
        <div className="text-xs text-zinc-600 mb-3">
          🛑 Hard: {data.violations.summary.hard} ·
          ⚠️ Soft: {data.violations.summary.soft} ·
          ℹ️ Advisory: {data.violations.summary.advisory}
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tier</TableHead><TableHead>Rule</TableHead><TableHead>Employee</TableHead><TableHead>Trip</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.violations.violations||[]).slice(0,20).map((v,i) => (
              <TableRow key={i}>
                <TableCell><Badge className={v.tier === "hard" ? "bg-red-100 text-red-800" : v.tier === "soft" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}>{v.tier}</Badge></TableCell>
                <TableCell className="text-xs">{v.message}</TableCell>
                <TableCell>{v.employee_name}</TableCell>
                <TableCell className="text-xs">{v.purpose}</TableCell>
              </TableRow>
            ))}
            {(data.violations.violations||[]).length === 0 &&
              <TableRow><TableCell colSpan={4} className="text-center text-emerald-600 py-4">All clean — no violations in this window.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </SectionCard>
    </div>
  );
}
function Kpi({ label, value }) {
  return (
    <Card className="p-4" data-testid={`trv-kpi-${label.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
      <div className="text-[11px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}
function MiniTable({ rows, cols, empty }) {
  if (!rows || rows.length === 0) return <div className="text-zinc-500 text-sm py-2">{empty}</div>;
  return (
    <Table>
      <TableHeader><TableRow>{cols.map(c => <TableHead key={c[0]}>{c[1]}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.slice(0, 10).map((r,i) => (
          <TableRow key={i}>{cols.map(c => <TableCell key={c[0]}>{c[2] ? c[2](r[c[0]]) : r[c[0]]}</TableCell>)}</TableRow>
        ))}
      </TableBody>
    </Table>
  );
}


// ───────────────────────────── Partner Uploads ──────────────────────────
function PartnerUploadsTab() {
  const [tripId, setTripId] = useState("");
  const [filename, setFilename] = useState("");
  const [amount, setAmount] = useState("");
  const [gst, setGst] = useState("");
  const [file, setFile] = useState(null);

  const upload = async () => {
    if (!tripId || !file) { toast.error("Trip ID and file required"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1];
      try {
        await api.post("/travel/partner/invoices", {
          travel_request_id: tripId, filename: file.name,
          content_type: file.type || "application/pdf", data_base64: b64,
          invoice_amount: parseFloat(amount || "0"), gst_amount: parseFloat(gst || "0"),
        });
        toast.success("Uploaded"); setFile(null); setFilename("");
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <SectionCard title="Upload booking invoice / ticket" subtitle="Travel-partner portal — invoices link to trip + booking." icon={Receipt}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <div><Label>Trip ID</Label><Input value={tripId} onChange={e => setTripId(e.target.value)} placeholder="Paste trip UUID" data-testid="partner-trip-id"/></div>
        <div><Label>Invoice amount (₹)</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0"/></div>
        <div><Label>GST amount (₹)</Label><Input type="number" value={gst} onChange={e => setGst(e.target.value)} placeholder="0"/></div>
        <div><Label>Document (PDF, ≤5 MB)</Label>
          <Input type="file" accept=".pdf,.jpg,.png" onChange={e => { setFile(e.target.files?.[0]); setFilename(e.target.files?.[0]?.name || ""); }} data-testid="partner-file-input"/>
          {filename && <div className="text-xs text-zinc-500 mt-1">{filename}</div>}
        </div>
      </div>
      <div className="mt-4"><Button onClick={upload} data-testid="partner-upload-btn">Upload</Button></div>
    </SectionCard>
  );
}
